import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Table,
  makeStyles,
} from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import RatingStarIcon from 'assets/icons/svg/RatingStarIcon';
import { getBackgroundColorByRatingSum } from 'pages/PagePartnersRatingReport/helpers';
import { IPartnerRatingByPartnerUgo } from '../../../interfaces';
import { useExportPartnerRatingByPartnerUgoExcelReport, useGetPartnerRatingByPartnerUgo } from '../hooks';
import PartnerRatingDialog from './PartnerRatingDialog';
import { INomenclature } from '../interfaces';

const useStyles = makeStyles({
  tableHeadCell: {
    backgroundColor: '#f5f5f5',
  },
  tableHeadCellSticky: {
    position: 'sticky',
    backgroundColor: '#f5f5f5',
  },
  tableBodyRow: {
    cursor: 'pointer',

    '&:nth-of-type(odd) td': {
      backgroundColor: '#f9f9f9',
    },
    '&:nth-of-type(even) td': {
      backgroundColor: '#fff',
    },
  },
  tableBodyCellSticky: {
    position: 'sticky',
  },
});

interface IProps {
  data: { list: IPartnerRatingByPartnerUgo[]; totalCount: number };
  nomenclature: INomenclature;
  year: number;
  loading: boolean;
  success: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  onChangePage(pageNo: number): void;
}

const PartnersRating = ({
  data,
  nomenclature,
  year,
  error,
  loading,
  page,
  pageSize,
  success,
  onChangePage,
}: IProps) => {
  // Styles
  const classes = useStyles();

  const [selectedPartnerRating, setSelectedPartnerRating] = useState<IPartnerRatingByPartnerUgo | null>(null);
  const [partnerRatingDialogOpened, setOpenedPartnerRatingDialog] = useState(false);

  const {
    fetchPartnerRatingByPartnerUgo,
    data: realEstatesRatingByPartner,
    ...partnerRatingState
  } = useGetPartnerRatingByPartnerUgo();
  const { exportPartnerRatingByPartnerUgoExcelReport, ...partnerRatingByPartnerExcelState } =
    useExportPartnerRatingByPartnerUgoExcelReport();

  const params = {
    partnerGuid: selectedPartnerRating?.smrPartnerGuid,
    year,
    nomenclaturaGuid: nomenclature.nomenclaturaGuid,
  };
  const mapKey = Object.values(params).filter(Boolean).join('-');

  function handleClickPartnerRating(partnerRating: IPartnerRatingByPartnerUgo) {
    setSelectedPartnerRating(partnerRating);
    setOpenedPartnerRatingDialog(true);
  }

  function handleClosePartnerRatingDialog() {
    setSelectedPartnerRating(null);
    setOpenedPartnerRatingDialog(false);
  }

  useEffect(() => {
    if (!partnerRatingDialogOpened || !selectedPartnerRating) return;
    fetchPartnerRatingByPartnerUgo(selectedPartnerRating.smrPartnerGuid, {
      params: { year, nomenclaturaGuid: nomenclature.nomenclaturaGuid },
    });
  }, [partnerRatingDialogOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  const realEstatesRatingPartner = realEstatesRatingByPartner.get(mapKey) || null;

  return (
    <>
      {Boolean(loading) ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : Boolean(error) && !Boolean(data.list.length) ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Typography component="h3" variant="h5" align="center">
            {error}
          </Typography>
        </Box>
      ) : (
        success && (
          <>
            {data.list.length ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <Box
                        component={TableCell}
                        className={classes.tableHeadCellSticky}
                        style={{ minWidth: 145, left: 0 }}
                      >
                        <b>Место в рейтинге</b>
                      </Box>

                      {[
                        ['Партнер', 220, 145],
                        ['Оценка', 160, 365, 'center'],
                        ['Кол-во ДА', 150, 525, 'right'],
                      ].map(([label, width, offset, textAlign]) => (
                        <Box
                          key={label}
                          component={TableCell}
                          className={classes.tableHeadCellSticky}
                          // @ts-ignore
                          style={{ minWidth: width, left: offset, textAlign }}
                        >
                          <b>{label}</b>
                        </Box>
                      ))}

                      {['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'].map(
                        (month) => (
                          <TableCell
                            key={month}
                            className={classes.tableHeadCell}
                            style={{ minWidth: 100 }}
                            align="center"
                          >
                            <b>{month}</b>
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.list.map((partnerRating, index) => {
                      const { ratingPosition, smrPartnerName, applicationsCount, ratingSum, smrPartnerGuid, months } =
                        partnerRating;
                      const backgroundColor = getBackgroundColorByRatingSum(ratingSum);

                      return (
                        <TableRow
                          key={`${smrPartnerGuid}:${index}`}
                          className={classes.tableBodyRow}
                          hover
                          onClick={() => handleClickPartnerRating(partnerRating)}
                        >
                          <TableCell
                            className={classes.tableHeadCellSticky}
                            style={{ textAlign: 'center', minWidth: 145, left: 0 }}
                          >
                            <b>{ratingPosition}</b>
                          </TableCell>

                          {[
                            [smrPartnerName, 145],
                            [ratingSum, 365, 'center'],
                            [applicationsCount, 525, 'right'],
                          ].map(([value, offset, textAlign], index) => (
                            <TableCell
                              key={index}
                              className={classes.tableBodyCellSticky}
                              style={{
                                // @ts-ignore
                                textAlign,
                                left: offset,
                                backgroundColor,
                              }}
                            >
                              {index === 1 ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    gridGap: 8,
                                  }}
                                >
                                  <Rating icon={<RatingStarIcon />} value={ratingSum} readOnly precision={0.25} />
                                  {value}
                                </Box>
                              ) : (
                                value
                              )}
                            </TableCell>
                          ))}

                          {[...Array(12)].map((_, index) => {
                            const month = months.find((month) => +month.dateAt.split('-')[1] === index + 1);

                            return month ? (
                              <TableCell style={{ textAlign: 'center' }} key={month.dateAt}>
                                {Math.round(month.ratingSum * 100) / 100}
                              </TableCell>
                            ) : (
                              <TableCell key={index} style={{ textAlign: 'center' }}>
                                0
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography component="h3" variant="h5" align="center">
                  Данные отсутствуют
                </Typography>
              </Box>
            )}

            <PartnerRatingDialog
              {...partnerRatingState}
              data={realEstatesRatingPartner!}
              partnerRating={selectedPartnerRating!}
              partnersCount={data.totalCount}
              nomenclature={nomenclature}
              year={year}
              open={partnerRatingDialogOpened}
              exportExcelLoading={partnerRatingByPartnerExcelState.loading}
              onExportExcel={() =>
                exportPartnerRatingByPartnerUgoExcelReport(selectedPartnerRating!.smrPartnerGuid, { params })
              }
              onClose={handleClosePartnerRatingDialog}
            />
          </>
        )
      )}

      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <IconButton aria-label="Назад" disabled={loading || page === 1} onClick={() => onChangePage(page - 1)}>
          <KeyboardArrowLeft />
        </IconButton>

        <IconButton
          aria-label="Вперёд"
          disabled={loading || !data.totalCount || page === Math.ceil((data.totalCount || 0) / pageSize)}
          onClick={() => onChangePage(page + 1)}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </>
  );
};

export default PartnersRating;
